import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Sparkle, X, ArrowCircleUp, ArrowCircleDown } from "@phosphor-icons/react";

const PLAN_ORDER = ["basic", "standard", "pro"];

export default function BillingSection() {
  const [state, setState] = useState(null);
  const [config, setConfig] = useState(null);
  const [busy, setBusy] = useState(null); // plan key being changed
  const [cancelling, setCancelling] = useState(false);
  const [confirm, setConfirm] = useState(null); // {plan, direction}

  const load = () => {
    api.get("/billing/me").then((r) => setState(r.data)).catch(() => {});
    api.get("/billing/config").then((r) => setConfig(r.data)).catch(() => {});
  };
  useEffect(load, []);

  if (!state || !config) return null;

  const currentPlan = state.plan || "free";
  const status = state.plan_status || "free";
  const orderIdx = ["free", ...PLAN_ORDER].indexOf(currentPlan);
  const isPaid = currentPlan !== "free" && !["cancelled", "expired"].includes(status);

  const openConfirm = (planKey) => {
    const planIdx = ["free", ...PLAN_ORDER].indexOf(planKey);
    const direction = planIdx > orderIdx ? "upgrade" : "downgrade";
    setConfirm({ plan: planKey, direction });
  };

  const doChange = async () => {
    if (!confirm) return;
    setBusy(confirm.plan);
    try {
      const { data } = await api.post("/billing/change-plan", { plan: confirm.plan });
      toast.success(`Plan ${confirm.direction} scheduled — ${data.effective}`);
      setConfirm(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not change plan");
    } finally {
      setBusy(null);
    }
  };

  const doCancel = async () => {
    if (!window.confirm("Cancel subscription at the end of the current cycle? You will keep paid access until then.")) return;
    setCancelling(true);
    try {
      await api.post("/billing/cancel-subscription");
      toast.success("Subscription cancellation scheduled for end of cycle.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cancel");
    } finally {
      setCancelling(false);
    }
  };

  return (
    <div className="nb-card p-6 space-y-5" data-testid="billing-section">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-black">Subscription</h2>
          <p className="text-sm text-[#4A4A4A] font-semibold mt-1">
            Your plan: <span className="font-black capitalize text-black">{currentPlan}</span> ·{" "}
            <span className="font-black uppercase text-xs tracking-wider text-[#4361EE]">{status.replace("_", " ")}</span>
          </p>
          {status === "trial" && state.trial_ends_at && (
            <p className="text-xs text-[#4A4A4A] font-semibold mt-0.5">
              14-day free trial ends{" "}
              {new Date(state.trial_ends_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
            </p>
          )}
        </div>
      </div>

      {!isPaid && (
        <div className="text-sm font-semibold">
          You are on the free plan.{" "}
          <Link to="/pricing" className="underline text-[#4361EE] font-bold" data-testid="billing-start-trial">
            Start a 14-day free trial →
          </Link>
        </div>
      )}

      {isPaid && (
        <>
          <div className="grid sm:grid-cols-3 gap-3">
            {PLAN_ORDER.map((pk) => {
              const p = config.plans[pk];
              const isCurrent = pk === currentPlan;
              const planIdx = ["free", ...PLAN_ORDER].indexOf(pk);
              const isUpgrade = planIdx > orderIdx;
              return (
                <div
                  key={pk}
                  className={`border-2 border-black rounded-md p-3 ${isCurrent ? "bg-[#FFD166]" : "bg-white"}`}
                  data-testid={`change-plan-${pk}`}
                >
                  <div className="flex items-baseline justify-between">
                    <h4 className="font-heading text-base font-black capitalize">{pk}</h4>
                    <span className="font-black">₹{p.price_inr}</span>
                  </div>
                  <p className="text-xs text-[#4A4A4A] font-semibold mt-1">
                    {p.customer_limit} customers · {p.wa_msg_limit} msgs/mo
                  </p>
                  <button
                    disabled={isCurrent || busy === pk}
                    onClick={() => openConfirm(pk)}
                    className={`nb-btn mt-3 w-full py-1.5 text-xs font-black ${
                      isCurrent
                        ? "bg-gray-200 cursor-not-allowed"
                        : isUpgrade
                        ? "bg-[#06D6A0] text-white"
                        : "bg-white"
                    }`}
                    data-testid={`change-plan-${pk}-btn`}
                  >
                    {isCurrent ? "Current" : busy === pk ? "Updating…" : isUpgrade ? (
                      <span className="inline-flex items-center justify-center gap-1">
                        <ArrowCircleUp size={14} weight="bold" /> Upgrade
                      </span>
                    ) : (
                      <span className="inline-flex items-center justify-center gap-1">
                        <ArrowCircleDown size={14} weight="bold" /> Downgrade
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          <div className="flex items-center justify-between pt-3 border-t-2 border-black/10">
            <p className="text-xs text-[#4A4A4A] font-semibold max-w-md">
              Upgrades apply <span className="font-bold">immediately</span> with proration. Downgrades take effect at the <span className="font-bold">end of the current billing cycle</span>.
            </p>
            <button
              onClick={doCancel}
              disabled={cancelling}
              data-testid="cancel-subscription-btn"
              className="text-xs font-bold text-[#FF6B6B] underline hover:no-underline disabled:opacity-50"
            >
              {cancelling ? "Cancelling…" : "Cancel subscription"}
            </button>
          </div>
        </>
      )}

      {confirm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setConfirm(null)}>
          <div
            className="nb-card bg-white p-6 max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
            data-testid="change-plan-confirm"
          >
            <div className="flex items-start justify-between mb-3">
              <h3 className="font-heading text-xl font-black capitalize">
                {confirm.direction} to {confirm.plan}?
              </h3>
              <button onClick={() => setConfirm(null)} className="p-1" aria-label="close">
                <X size={18} weight="bold" />
              </button>
            </div>
            <p className="text-sm font-semibold text-[#4A4A4A] mb-5">
              {confirm.direction === "upgrade" ? (
                <>Your plan will change <b>immediately</b>. Razorpay will charge a prorated amount for the remainder of this cycle, and the new limits apply right away.</>
              ) : (
                <>You will keep <b>{currentPlan}</b> benefits until the end of your current billing cycle. The {confirm.plan} limits will kick in on the next renewal.</>
              )}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setConfirm(null)}
                className="nb-btn flex-1 bg-white py-2 font-black"
                data-testid="change-plan-cancel-btn"
              >
                Nevermind
              </button>
              <button
                onClick={doChange}
                disabled={busy === confirm.plan}
                className={`nb-btn flex-1 py-2 font-black text-white ${confirm.direction === "upgrade" ? "bg-[#06D6A0]" : "bg-[#4361EE]"}`}
                data-testid="change-plan-confirm-btn"
              >
                {busy === confirm.plan ? "Processing…" : `Yes, ${confirm.direction}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
